import { useState } from 'react'
import { 
  ShieldAlert, 
  ShieldCheck, 
  Shield, 
  UserPlus, 
  Trash2, 
  AlertCircle,
  CheckCircle2,
  XCircle,
  Mail,
  User,
  ChevronDown,
  Crown,
  Loader2
} from 'lucide-react'
import { SectionCard } from '@/components/shared/section-card'
import { DataTable } from '@/components/shared/data-table'
import { StatusBadge } from '@/components/shared/status-badge'
import { useAuth, useAdminUsers } from '@/hooks/useAuth'
import { useCollectionRecords } from '@/hooks'
import { formatDateTime } from '@/lib/format'
import { AdminRole, UserProfile } from '@/types'
import { cn } from '@/lib/utils'

const roleConfig: Record<AdminRole, { label: string; color: string; icon: any; description: string }> = {
  superadmin: {
    label: 'Super Admin',
    color: 'rose',
    icon: Crown,
    description: 'Full access. Can manage all admins and settings.',
  },
  admin: {
    label: 'Admin',
    color: 'orange',
    icon: ShieldCheck,
    description: 'Can manage content, products, settings. Cannot manage other admins.',
  },
  editor: {
    label: 'Editor',
    color: 'blue',
    icon: Shield,
    description: 'Can edit content, products, articles, classes, and banners only.',
  },
  viewer: {
    label: 'Viewer',
    color: 'stone',
    icon: User,
    description: 'View-only access to analytics, users, and support.',
  },
}

export function AdminManagementPage() {
  const { isSuperAdmin, adminProfile, user } = useAuth()
  const { admins, loading, error: adminError, createAdmin, updateAdminRole, removeAdmin } = useAdminUsers()
  
  const [showAddModal, setShowAddModal] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [newAdminEmail, setNewAdminEmail] = useState('')
  const [newAdminRole, setNewAdminRole] = useState<AdminRole>('editor')
  const [newAdminName, setNewAdminName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  // Assign existing user state
  const [assignEmail, setAssignEmail] = useState('')
  const [assignRole, setAssignRole] = useState<AdminRole>('editor')
  const [isAssigning, setIsAssigning] = useState(false)

  // Fetch users for searching/selecting in assign modal
  const { data: allUsers } = useCollectionRecords<UserProfile>(['users'], {
    orderByField: 'updatedAt',
    limitCount: 500,
  })

  // Check if current user is the hardcoded superadmin
  const isHardcodedSuperAdmin = user?.email === 'khafidbachtiar@gmail.com'
  const hasAccess = isSuperAdmin || isHardcodedSuperAdmin

  // Only superadmin can access this page
  if (!hasAccess) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="mx-auto h-16 w-16 text-rose-500 mb-4" />
          <h2 className="text-xl font-bold text-white">Access Denied</h2>
          <p className="text-stone-400 mt-2">Only Super Admin can manage admin access.</p>
          <p className="text-stone-500 text-sm mt-4">
            Current role: {adminProfile?.role || 'unknown'}
          </p>
        </div>
      </div>
    )
  }

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    setIsSubmitting(true)

    try {
      // Validation
      if (!newAdminEmail || !newAdminEmail.includes('@')) {
        throw new Error('Please enter a valid email address')
      }

      // Check if already exists
      const existingAdmin = admins.find(a => a.email?.toLowerCase() === newAdminEmail.toLowerCase())
      if (existingAdmin) {
        throw new Error('An admin with this email already exists')
      }

      await createAdmin(newAdminEmail, newAdminRole, newAdminName || undefined)
      
      setSuccess(`Successfully invited ${newAdminEmail} as ${roleConfig[newAdminRole].label}`)
      setNewAdminEmail('')
      setNewAdminName('')
      setNewAdminRole('editor')
      setShowAddModal(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create admin')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRoleChange = async (uid: string, newRole: AdminRole, currentRole: AdminRole) => {
    // Prevent self-demotion from superadmin
    if (uid === adminProfile?.uid && currentRole === 'superadmin' && newRole !== 'superadmin') {
      alert('You cannot demote yourself from Super Admin')
      return
    }

    try {
      await updateAdminRole(uid, newRole)
      setSuccess(`Role updated successfully`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role')
    }
  }

  const handleAssignExisting = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsAssigning(true)
    try {
      if (!assignEmail || !assignEmail.includes('@')) {
        throw new Error('Masukkan alamat email yang valid')
      }
      // Check if already an admin
      const existing = admins.find(a => a.email?.toLowerCase() === assignEmail.toLowerCase())
      if (existing) {
        throw new Error(`${assignEmail} sudah terdaftar sebagai ${existing.role}`)
      }
      // Create invited record — will be activated on first login
      await createAdmin(assignEmail, assignRole)
      setSuccess(`${assignEmail} berhasil di-assign sebagai ${roleConfig[assignRole].label}. Mereka harus login dengan akun Google ini.`)
      setAssignEmail('')
      setAssignRole('editor')
      setShowAssignModal(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal assign user')
    } finally {
      setIsAssigning(false)
    }
  }

  const handleRemoveAdmin = async (uid: string, email: string) => {
    // Prevent self-removal
    if (uid === adminProfile?.uid) {
      alert('You cannot remove yourself')
      return
    }

    if (!confirm(`Are you sure you want to remove ${email} from admin access?`)) {
      return
    }

    try {
      await removeAdmin(uid)
      setSuccess(`Admin access removed for ${email}`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove admin')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-[36px] border border-rose-500/20 bg-gradient-to-br from-rose-500/10 to-orange-500/5 p-8">
        <div className="inline-flex rounded-full border border-rose-500/30 bg-rose-500/10 px-4 py-1 text-xs uppercase tracking-[0.24em] text-rose-400">
          Super Admin Only
        </div>
        <h2 className="mt-5 max-w-2xl text-4xl font-semibold leading-tight text-white">
          Admin Access Management
        </h2>
        <p className="mt-4 max-w-2xl text-base leading-7 text-stone-400">
          Manage admin roles and permissions. Super Admins can promote users to Admin or Editor roles. 
          Admins can only manage Editors and Viewers.
        </p>
        
        {/* Debug Info */}
        <div className="mt-4 p-4 rounded-xl bg-stone-900/50 border border-stone-800">
          <p className="text-sm text-stone-400">
            Logged in as: <span className="text-white font-medium">{user?.email}</span>
          </p>
          <p className="text-sm text-stone-400 mt-1">
            Role: <span className="text-rose-400 font-medium">{adminProfile?.role || 'Not set'}</span>
          </p>
        </div>

        <div className="mt-6 flex gap-4">
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 rounded-xl bg-rose-500 px-5 py-3 text-sm font-bold text-white hover:bg-rose-600 transition shadow-lg shadow-rose-500/20"
          >
            <UserPlus className="h-4 w-4" />
            Add New Admin
          </button>
          <button
            onClick={() => setShowAssignModal(true)}
            className="flex items-center gap-2 rounded-xl border border-stone-700 bg-stone-900 px-5 py-3 text-sm font-bold text-stone-300 hover:bg-stone-800 transition"
          >
            <Mail className="h-4 w-4" />
            Assign Existing User
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 flex items-center gap-3 text-rose-400">
          <XCircle className="h-5 w-5" />
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 flex items-center gap-3 text-emerald-400">
          <CheckCircle2 className="h-5 w-5" />
          {success}
        </div>
      )}

      {/* Role Legend */}
      <SectionCard title="Role Permissions" description="Understanding admin role hierarchy">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {(Object.keys(roleConfig) as AdminRole[]).map((role) => {
            const config = roleConfig[role]
            const Icon = config.icon
            return (
              <div 
                key={role} 
                className={cn(
                  "rounded-2xl border p-4",
                  `border-${config.color}-500/20 bg-${config.color}-500/5`
                )}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={cn("h-5 w-5", `text-${config.color}-500`)} />
                  <span className={cn("font-bold", `text-${config.color}-400`)}>
                    {config.label}
                  </span>
                </div>
                <p className="text-xs text-stone-500 leading-relaxed">
                  {config.description}
                </p>
              </div>
            )
          })}
        </div>
      </SectionCard>

      {/* Admin List */}
      <SectionCard 
        title="Admin Users" 
        description="Current admins and their access levels"
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 text-orange-500 animate-spin" />
            <span className="ml-3 text-stone-400">Loading admins...</span>
          </div>
        ) : adminError ? (
          <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 flex items-center gap-3 text-rose-400">
            <AlertCircle className="h-5 w-5" />
            {adminError}
          </div>
        ) : (
          <DataTable
            rows={admins}
            columns={[
              {
                header: 'Admin',
                render: (row) => (
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "h-10 w-10 rounded-full flex items-center justify-center font-bold text-sm",
                      row.role === 'superadmin' ? 'bg-rose-500/20 text-rose-400' :
                      row.role === 'admin' ? 'bg-orange-500/20 text-orange-400' :
                      row.role === 'editor' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-stone-700 text-stone-400'
                    )}>
                      {(row.displayName || row.email || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{row.displayName || 'Unnamed'}</p>
                      <p className="text-xs text-stone-500">{row.email}</p>
                    </div>
                  </div>
                ),
              },
              {
                header: 'Role',
                render: (row) => {
                  const RoleIcon = roleConfig[row.role as AdminRole]?.icon || User
                  return (
                    <div className="flex items-center gap-2">
                      <RoleIcon className={cn(
                        "h-4 w-4",
                        row.role === 'superadmin' ? 'text-rose-400' :
                        row.role === 'admin' ? 'text-orange-400' :
                        row.role === 'editor' ? 'text-blue-400' :
                        'text-stone-400'
                      )} />
                      <StatusBadge value={row.role} />
                    </div>
                  )
                },
              },
              {
                header: 'Status',
                render: (row) => <StatusBadge value={row.status || 'active'} />,
              },
              {
                header: 'Last Updated',
                render: (row) => (
                  <span className="text-stone-400 text-sm">
                    {formatDateTime(row.updatedAt)}
                  </span>
                ),
              },
              {
                header: 'Actions',
                render: (row) => (
                  <div className="flex items-center gap-2">
                    {/* Role Change Dropdown - Only for non-superadmins or if current user is superadmin */}
                    {row.uid !== adminProfile?.uid && (
                      <select
                        value={row.role}
                        onChange={(e) => handleRoleChange(row.uid, e.target.value as AdminRole, row.role as AdminRole)}
                        className="rounded-lg border border-stone-700 bg-stone-900 px-3 py-1.5 text-sm text-stone-300 focus:border-orange-500 focus:outline-none"
                      >
                        <option value="viewer">Viewer</option>
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                        <option value="superadmin">Super Admin</option>
                      </select>
                    )}
                    
                    {/* Remove Button - Cannot remove self or other superadmins */}
                    {row.uid !== adminProfile?.uid && row.role !== 'superadmin' && (
                      <button
                        onClick={() => handleRemoveAdmin(row.uid, row.email || '')}
                        className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-2 text-rose-400 hover:bg-rose-500/20 transition"
                        title="Remove admin access"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    
                    {row.role === 'superadmin' && (
                      <span className="text-xs text-stone-600 italic">Protected</span>
                    )}
                  </div>
                ),
              },
            ]}
            emptyTitle="No admin users"
            emptyDescription="Add your first admin to delegate management responsibilities."
          />
        )}
      </SectionCard>

      {/* Permission Matrix */}
      <SectionCard title="Permission Matrix" description="Detailed breakdown of what each role can access">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-800">
                <th className="text-left py-3 px-4 text-stone-400 font-medium">Feature</th>
                <th className="text-center py-3 px-4 text-rose-400 font-medium">Super Admin</th>
                <th className="text-center py-3 px-4 text-orange-400 font-medium">Admin</th>
                <th className="text-center py-3 px-4 text-blue-400 font-medium">Editor</th>
                <th className="text-center py-3 px-4 text-stone-400 font-medium">Viewer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-800">
              {[
                { feature: 'Manage Super Admins', superadmin: true, admin: false, editor: false, viewer: false },
                { feature: 'Manage Admins', superadmin: true, admin: false, editor: false, viewer: false },
                { feature: 'Manage Editors', superadmin: true, admin: true, editor: false, viewer: false },
                { feature: 'System Settings', superadmin: true, admin: true, editor: false, viewer: false },
                { feature: 'View Analytics', superadmin: true, admin: true, editor: false, viewer: true },
                { feature: 'View Users', superadmin: true, admin: true, editor: false, viewer: true },
                { feature: 'Edit Products', superadmin: true, admin: true, editor: true, viewer: false },
                { feature: 'Edit Articles', superadmin: true, admin: true, editor: true, viewer: false },
                { feature: 'Edit Classes', superadmin: true, admin: true, editor: true, viewer: false },
                { feature: 'Edit Banners', superadmin: true, admin: true, editor: true, viewer: false },
                { feature: 'View Support Tickets', superadmin: true, admin: true, editor: false, viewer: true },
              ].map((row, idx) => (
                <tr key={idx} className="hover:bg-stone-900/50">
                  <td className="py-3 px-4 text-stone-300">{row.feature}</td>
                  <td className="text-center py-3 px-4">
                    {row.superadmin ? <CheckCircle2 className="h-5 w-5 text-emerald-500 mx-auto" /> : <XCircle className="h-5 w-5 text-stone-700 mx-auto" />}
                  </td>
                  <td className="text-center py-3 px-4">
                    {row.admin ? <CheckCircle2 className="h-5 w-5 text-emerald-500 mx-auto" /> : <XCircle className="h-5 w-5 text-stone-700 mx-auto" />}
                  </td>
                  <td className="text-center py-3 px-4">
                    {row.editor ? <CheckCircle2 className="h-5 w-5 text-emerald-500 mx-auto" /> : <XCircle className="h-5 w-5 text-stone-700 mx-auto" />}
                  </td>
                  <td className="text-center py-3 px-4">
                    {row.viewer ? <CheckCircle2 className="h-5 w-5 text-emerald-500 mx-auto" /> : <XCircle className="h-5 w-5 text-stone-700 mx-auto" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Add Admin Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-3xl border border-stone-800 bg-[#141414] p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-1">Add New Admin</h3>
            <p className="text-sm text-stone-500 mb-6">Invite a user to manage the dashboard</p>
            
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-stone-500" />
                  <input
                    type="email"
                    value={newAdminEmail}
                    onChange={(e) => setNewAdminEmail(e.target.value)}
                    placeholder="admin@example.com"
                    className="w-full rounded-xl border border-stone-800 bg-stone-900 pl-10 pr-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-300 mb-2">
                  Display Name (Optional)
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-stone-500" />
                  <input
                    type="text"
                    value={newAdminName}
                    onChange={(e) => setNewAdminName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full rounded-xl border border-stone-800 bg-stone-900 pl-10 pr-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-300 mb-2">
                  Role
                </label>
                <div className="relative">
                  <select
                    value={newAdminRole}
                    onChange={(e) => setNewAdminRole(e.target.value as AdminRole)}
                    className="w-full rounded-xl border border-stone-800 bg-stone-900 px-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none appearance-none"
                  >
                    <option value="viewer">Viewer — View-only access</option>
                    <option value="editor">Editor — Content &amp; Products</option>
                    <option value="admin">Admin — Full (except manage admins)</option>
                    <option value="superadmin">⚠️ Super Admin — Full access</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-stone-500 pointer-events-none" />
                </div>
                <p className="mt-2 text-xs text-stone-500">
                  {roleConfig[newAdminRole].description}
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 rounded-xl border border-stone-700 bg-stone-900 py-2.5 text-sm font-medium text-stone-300 hover:bg-stone-800 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 rounded-xl bg-orange-500 py-2.5 text-sm font-bold text-white hover:bg-orange-600 transition disabled:opacity-50"
                >
                  {isSubmitting ? 'Adding...' : 'Add Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Existing User Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-3xl border border-stone-800 bg-[#141414] p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-1">Assign Existing User</h3>
            <p className="text-sm text-stone-500 mb-6">
              Masukkan email akun Google yang sudah terdaftar di Firebase. Setelah di-assign, user tersebut bisa login sebagai admin.
            </p>
            
            <form onSubmit={handleAssignExisting} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-300 mb-2">Email Firebase User</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-stone-500" />
                  <input
                    type="email"
                    list="registered-users"
                    value={assignEmail}
                    onChange={(e) => setAssignEmail(e.target.value)}
                    placeholder="Search name or select from list..."
                    className="w-full rounded-xl border border-stone-800 bg-stone-900 pl-10 pr-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none"
                    required
                    autoComplete="off"
                  />
                  <datalist id="registered-users">
                    {allUsers
                      ?.filter((u) => u.email)
                      .map((u) => (
                        <option key={u.uid} value={u.email}>
                          {u.displayName ? `${u.displayName} (${u.email})` : u.email}
                        </option>
                      ))}
                  </datalist>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-300 mb-2">Role</label>
                <div className="relative">
                  <select
                    value={assignRole}
                    onChange={(e) => setAssignRole(e.target.value as AdminRole)}
                    className="w-full rounded-xl border border-stone-800 bg-stone-900 px-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none appearance-none"
                  >
                    <option value="viewer">Viewer — View-only</option>
                    <option value="editor">Editor — Content &amp; Products</option>
                    <option value="admin">Admin — Full (except manage admins)</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-stone-500 pointer-events-none" />
                </div>
                <p className="mt-2 text-xs text-stone-500">{roleConfig[assignRole].description}</p>
              </div>

              <div className="rounded-2xl bg-amber-500/10 border border-amber-500/20 p-3">
                <p className="text-xs text-amber-400 leading-relaxed">
                  ⚠️ User harus login ke dashboard menggunakan akun Google yang sama agar record ini aktif.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowAssignModal(false); setAssignEmail(''); }}
                  className="flex-1 rounded-xl border border-stone-700 bg-stone-900 py-2.5 text-sm font-medium text-stone-300 hover:bg-stone-800 transition"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isAssigning}
                  className="flex-1 rounded-xl bg-orange-500 py-2.5 text-sm font-bold text-white hover:bg-orange-600 transition disabled:opacity-50"
                >
                  {isAssigning ? 'Assigning...' : 'Assign Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
